import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { UserCircle, Award, ChevronLeft, ChevronRight, Star, X, ChevronRight as ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function isDirector(monitor) {
  return (monitor.especialidad || "").toLowerCase().includes("director") ||
         (monitor.rol || "").toLowerCase().includes("director");
}

// ── Modal de detalle completo ─────────────────────────────────────────────────
function MonitorModal({ monitor, onClose }) {
  const imgUrl = getImageUrl(monitor.foto_url);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const directorStyle = isDirector(monitor);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="monitor-modal-overlay"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-sm shadow-2xl relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="monitor-modal"
      >
        {/* Color bar top */}
        <div
          className="h-1.5 w-full"
          style={{ background: directorStyle ? "linear-gradient(90deg,#0072A4,#00AEEF)" : "#0072A4" }}
        />

        {/* Close */}
        <button
          onClick={onClose}
          data-testid="monitor-modal-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F0F8FF] flex items-center justify-center hover:bg-[#E0F0FF] transition-colors z-10"
          aria-label="Cerrar"
        >
          <X size={16} color="#475569" />
        </button>

        <div className="p-7 flex flex-col items-center text-center">
          {/* Photo */}
          <div
            className="w-24 h-24 rounded-2xl overflow-hidden border-4 shadow-md flex items-center justify-center mb-4"
            style={{
              borderColor: directorStyle ? "rgba(0,114,164,0.25)" : "rgba(0,114,164,0.15)",
              background: "#F0F8FF",
            }}
          >
            {imgUrl ? (
              <img src={imgUrl} alt={monitor.nombre} className="w-full h-full object-cover" />
            ) : (
              <UserCircle size={48} color="#0072A4" />
            )}
          </div>

          {/* Badge */}
          {directorStyle ? (
            <div className="inline-flex items-center gap-1.5 bg-[#F5C518] text-[#0F172A] font-['Montserrat'] font-bold text-[10px] px-3 py-1 rounded-full uppercase tracking-wider mb-3">
              <Star size={10} fill="#0F172A" color="#0F172A" />
              {monitor.especialidad}
            </div>
          ) : (
            <div className="inline-block bg-[#0072A4] text-white font-['Montserrat'] font-bold text-[10px] px-3 py-1 rounded-full uppercase tracking-wider mb-3">
              {monitor.especialidad}
            </div>
          )}

          {/* Name */}
          <h3 className="font-['Montserrat'] font-black text-lg text-[#0F172A] mb-3 leading-tight">
            {monitor.nombre}
          </h3>

          {/* Full description */}
          {monitor.descripcion && (
            <p className="text-[#475569] font-['Montserrat'] text-sm leading-relaxed">
              {monitor.descripcion}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Director Card ─────────────────────────────────────────────────────────────
function DirectorCard({ monitor, onOpen }) {
  const imgUrl = getImageUrl(monitor.foto_url);
  return (
    <div
      data-testid={`monitor-director-${monitor._id}`}
      onClick={() => onOpen(monitor)}
      className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-gradient-to-br from-[#0072A4] to-[#005A80] rounded-3xl p-7 sm:p-8 text-white shadow-xl mb-10 max-w-xl mx-auto cursor-pointer hover:shadow-2xl transition-shadow group"
    >
      {/* Photo */}
      <div className="flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden border-4 border-white/30 shadow-lg bg-white/20 flex items-center justify-center">
        {imgUrl ? (
          <img src={imgUrl} alt={monitor.nombre} className="w-full h-full object-cover" />
        ) : (
          <UserCircle size={56} color="white" />
        )}
      </div>

      {/* Info */}
      <div className="text-center sm:text-left flex-1">
        <div className="inline-flex items-center gap-1.5 bg-[#F5C518] text-[#0F172A] font-['Montserrat'] font-bold text-[10px] px-3 py-1 rounded-full uppercase tracking-wider mb-3">
          <Star size={10} fill="#0F172A" color="#0F172A" />
          {monitor.especialidad}
        </div>
        <h3 className="font-['Montserrat'] font-black text-lg text-white mb-2">{monitor.nombre}</h3>
        {monitor.descripcion && (
          <p className="text-white/80 font-['Montserrat'] text-xs leading-relaxed line-clamp-2">{monitor.descripcion}</p>
        )}
        <span className="inline-flex items-center gap-1 text-white/60 font-['Montserrat'] text-[10px] font-semibold mt-2 group-hover:text-white/90 transition-colors">
          Ver bio completa <ArrowRight size={10} />
        </span>
      </div>
    </div>
  );
}

// ── Monitor Card (carrusel) ───────────────────────────────────────────────────
function MonitorCard({ monitor, onOpen }) {
  const imgUrl = getImageUrl(monitor.foto_url);
  return (
    <div
      data-testid={`monitor-card-${monitor._id}`}
      onClick={() => onOpen(monitor)}
      className="flex-shrink-0 text-center bg-[#F0F8FF] rounded-2xl p-5 border border-[#0072A4]/10 hover:shadow-md hover:border-[#0072A4]/30 transition-all duration-200 cursor-pointer group"
      style={{ width: "min(180px, 70vw)" }}
    >
      <div className="w-16 h-16 rounded-xl mx-auto mb-3 overflow-hidden border-2 border-white shadow-sm bg-[#0072A4]/10 flex items-center justify-center">
        {imgUrl ? (
          <img src={imgUrl} alt={monitor.nombre} className="w-full h-full object-cover" />
        ) : (
          <UserCircle size={36} color="#0072A4" />
        )}
      </div>
      <h3 className="font-['Montserrat'] font-bold text-sm text-[#0F172A] mb-1.5 leading-snug">{monitor.nombre}</h3>
      <div className="inline-block bg-[#0072A4] text-white font-['Montserrat'] font-bold text-[9px] px-2.5 py-1 rounded-full uppercase tracking-wider">
        {monitor.especialidad}
      </div>
      {monitor.descripcion && (
        <p className="text-[#475569] font-['Montserrat'] text-[11px] leading-relaxed mt-2 line-clamp-2">{monitor.descripcion}</p>
      )}
      <p className="text-[#0072A4] font-['Montserrat'] text-[10px] font-bold mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        Ver más →
      </p>
    </div>
  );
}

// ── Main Section ──────────────────────────────────────────────────────────────
export default function MonitorsSection({ monitors_content = {} }) {
  const [monitors, setMonitors] = useState([]);
  const [selected, setSelected] = useState(null);
  const trackRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/monitors`)
      .then(res => setMonitors(res.data))
      .catch(() => {});
  }, []);

  if (!monitors.length) return null;

  const director = monitors.find(isDirector) || monitors[0];
  const rest = monitors.filter(m => m._id !== director._id);

  const scroll = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const cardW = track.children[0]?.offsetWidth + 16 || 196;
    track.scrollBy({ left: dir * cardW * 2, behavior: "smooth" });
  };

  // Dynamic texts with fallbacks
  const sectionLabel = monitors_content.section_label || "Nuestro Equipo";
  const sectionTitle = monitors_content.title || "Los mejores monitores";
  const sectionSubtitle = monitors_content.subtitle || "Profesionales titulados y apasionados de la educación al aire libre.";

  return (
    <section id="monitores" className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 bg-[#0072A4]/10 rounded-full px-5 py-2 mb-5">
            <Award size={14} color="#0072A4" />
            <span className="text-[#0072A4] font-['Montserrat'] font-bold text-[10px] uppercase tracking-[0.18em]">
              {sectionLabel}
            </span>
          </div>
          <h2
            className="font-['Montserrat'] font-extrabold text-[#0F172A] mb-4"
            style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.625rem)", letterSpacing: "-0.01em", lineHeight: 1.2 }}
            data-testid="monitors-title"
          >
            {sectionTitle}
          </h2>
          <p className="text-[#475569] font-['Montserrat'] text-sm max-w-md mx-auto">
            {sectionSubtitle}
          </p>
        </div>

        {/* Director — featured */}
        <DirectorCard monitor={director} onOpen={setSelected} />

        {/* Rest — carousel */}
        {rest.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4 max-w-xl mx-auto">
              <span className="font-['Montserrat'] font-semibold text-xs text-[#94A3B8] uppercase tracking-wider">
                Monitores del campus
              </span>
              <div className="flex gap-1.5">
                <button
                  onClick={() => scroll(-1)}
                  className="w-7 h-7 rounded-full border border-[#E2E8F0] bg-white flex items-center justify-center hover:border-[#0072A4] hover:bg-[#F0F8FF] transition-colors"
                  aria-label="Anterior"
                >
                  <ChevronLeft size={14} color="#475569" />
                </button>
                <button
                  onClick={() => scroll(1)}
                  className="w-7 h-7 rounded-full border border-[#E2E8F0] bg-white flex items-center justify-center hover:border-[#0072A4] hover:bg-[#F0F8FF] transition-colors"
                  aria-label="Siguiente"
                >
                  <ChevronRight size={14} color="#475569" />
                </button>
              </div>
            </div>

            <div
              ref={trackRef}
              className="flex gap-4 overflow-x-auto pb-2 max-w-xl mx-auto"
              style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {rest.map((monitor) => (
                <div key={monitor._id} style={{ scrollSnapAlign: "start" }}>
                  <MonitorCard monitor={monitor} onOpen={setSelected} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {selected && <MonitorModal monitor={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
